import React, { useState } from 'react';
import { Space, Empty } from 'antd';
import { ResponsiveTreeMap } from '@nivo/treemap';
import { SectorIndustryHierarchy } from "../../constants";

const SectorIndustryTreemap = ({ industries, height = 500 }) => {
  const [currentPath, setCurrentPath] = useState(null);

  const colorGradients = [
    { id: 'gradient-0', colors: ['#2451B7', '#6691E7'] },
    { id: 'gradient-1', colors: ['#45B7B8', '#92E7E7'] },
    { id: 'gradient-2', colors: ['#4BB717', '#96E768'] },
    { id: 'gradient-3', colors: ['#B745B7', '#E792E7'] },
    { id: 'gradient-4', colors: ['#B72451', '#E76691'] },
    { id: 'gradient-5', colors: ['#B7B724', '#E7E768'] },
    { id: 'gradient-6', colors: ['#24B745', '#68E792'] },
    { id: 'gradient-7', colors: ['#4524B7', '#9168E7'] },
    { id: 'gradient-8', colors: ['#B74524', '#E79168'] },
    { id: 'gradient-9', colors: ['#24B7B7', '#68E7E7'] },
    { id: 'gradient-10', colors: ['#7B24B7', '#C168E7'] },
  ];

  const sectorGradientMap = new Map();
  Object.keys(SectorIndustryHierarchy).forEach((sector, index) => {
    sectorGradientMap.set(sector, colorGradients[index % colorGradients.length].id);
  });

  const buildTreeData = () => {
    const treeData = {
      name: 'root',
      children: Object.entries(SectorIndustryHierarchy).map(([sectorKey, industryArray]) => {
        const sectorValue = industryArray.reduce((sum, industryKey) => sum + (industries[industryKey] || 0), 0);

        if (sectorValue === 0) return null;

        return {
          name: sectorKey,
          id: sectorKey,
          value: sectorValue,
          gradientId: sectorGradientMap.get(sectorKey),
          children: industryArray
            .filter(industryKey => industries[industryKey] > 0)
            .map(industryKey => ({
              name: industryKey,
              id: `${sectorKey}-${industryKey}`,
              value: industries[industryKey],
              gradientId: sectorGradientMap.get(sectorKey),
            })),
        };
      }).filter(Boolean),
    };
    return treeData;
  };

  const handleNodeClick = node => {
    setCurrentPath(currentPath === node.id ? null : node.id);
  };

  if (!industries || typeof industries !== 'object' || Object.keys(industries).length === 0) {
    return <Empty description="No Data Available" />;
  }

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <div style={{ height, width: '100%' }}>
        <ResponsiveTreeMap
          data={buildTreeData()}
          identity="id"
          value="value"
          tile="binary"
          valueFormat=".2f"
          margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
          label={node => {
            // Only show labels for sectors (depth 1) or large tiles in industries (depth 2)
            if (node.depth === 1) return node.name; // Sector labels
            if (node.depth === 2 && node.value > 2) return node.name; // Industry labels only if tile size is large
            return '';
          }}
          labelSkipSize={20} // Avoid clutter on smaller tiles
          colors={{ scheme: "yellow_green_blue" }}
          nodeOpacity={1}
          labelTextColor={{ from: 'color', modifiers: [['darker', 1.2]] }}
          borderColor={{ from: 'color', modifiers: [['opacity', 0.5]] }}
          onClick={handleNodeClick}
          animate={true}
          motionConfig="gentle"
          defs={colorGradients.map(({ id, colors }) => ({
            id,
            type: 'linearGradient',
            colors: [
              { offset: 0, color: colors[0] },
              { offset: 100, color: colors[1] },
            ],
          }))}
          fill={[
            {
              match: { depth: 1 },
              id: node => node.data.gradientId,
            },
          ]}
        />
      </div>
      <div style={{ textAlign: 'center' }}>
        {currentPath && (
          <button
            onClick={() => setCurrentPath(null)}
            className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
          >
            Reset View
          </button>
        )}
      </div>
    </Space>
  );
};

export default SectorIndustryTreemap;
